import { stripClipPreviewParams } from '@src/components/content-components/clipPreviewUrlState';
import { seedFromPreviewClip } from '@src/services/clipLoad/previewClipSeed';
import { useStudioHandoff } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Studio } from '@src/types.ts/general';
import { recordLastEditedClip } from '@src/utility/lastEditedClip';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { PreviewClip } from '@src/services/clipLoad/previewClipSeed';
import type { AssociatedVideo, VideoClip } from '@src/types.ts/story';

/** The clip preview a navigation leaves from. */
export type PreviewSession = {
  activeVariant: PreviewClip;
  mainClip: PreviewClip;
};

interface NavigateOptions {
  /**
   * Transcript display language to open Creator Studio with (`null`/omitted
   * = Original). Written to the `?lang=` URL param that Creator Studio's
   * LeftSidePanel reads on mount, so the language selected in Content
   * Studio's clip-preview transcript carries across the navigation.
   */
  language?: string | null;
  /** Hands the preview's clip to Creator Studio so it opens without a load. */
  session?: PreviewSession;
}

type ReturnValue = (
  video: VideoClip | AssociatedVideo,
  options?: NavigateOptions,
) => void;

export function useNavigateToCreatorStudio(): ReturnValue {
  const videoCreator = useVideoCreatorStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handOffPreviewClip = useHandOffPreviewClip();

  const handleNavigate: ReturnValue = (video, options) => {
    searchParams.set('videoId', video.id!);

    // Remember this as the story's last-edited clip so Content Studio's
    // "Edit Video" button can reopen it later.
    recordLastEditedClip(
      videoCreator.story?.id ?? searchParams.get('storyId'),
      video.id,
    );

    if (options && 'language' in options) {
      if (options.language) searchParams.set('lang', options.language);
      else searchParams.delete('lang');
    }

    const keepsLoadedClip = videoCreator.prepareCreatorStudioNavigation(
      video.id,
    );
    if (!keepsLoadedClip && options?.session) {
      handOffPreviewClip(options.session);
    }

    // PLAT-10948 — the clip preview's params must not ride into Creator Studio:
    // they'd survive the trip back to Content Studio and reopen the preview.
    navigate({
      pathname: `/${Studio.creator}`,
      search: stripClipPreviewParams(searchParams).toString(),
    });
  };

  return handleNavigate;
}

/**
 * PLAT-11911 — hands the preview's active variant to Creator Studio's next
 * `loadVideo`, and records it as last edited so "Edit Video" opens it. Call
 * only once the preview's pending save has landed.
 */
export function useHandOffPreviewClip(): (session: PreviewSession) => void {
  const videoCreator = useVideoCreatorStore();
  const studioHandoff = useStudioHandoff();

  return useCallback(
    ({ activeVariant, mainClip }: PreviewSession) => {
      recordLastEditedClip(videoCreator.story?.id, activeVariant.id);
      // Creator Studio reopens its own copy; a seed would outlive it unused.
      if (activeVariant.id === videoCreator.currentVideo?.id) {
        studioHandoff.setSeed(null);
        return;
      }
      studioHandoff.setSeed(seedFromPreviewClip(activeVariant, mainClip));
    },
    [videoCreator, studioHandoff],
  );
}
